import React from 'react';

/**
 * Reusable pagination component — place at top AND bottom of lists.
 *
 * Props:
 *   total      - total number of items
 *   page       - current page (1-based)
 *   perPage    - items per page
 *   onPageChange    - (newPage) => void
 *   onPerPageChange - (newPerPage) => void
 *   theme      - 'portal' (default) or 'website'
 */
function Pagination({ total, page, perPage, onPageChange, onPerPageChange, theme = 'portal' }) {
  if (total === 0) return null;

  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const start = Math.min((page - 1) * perPage + 1, total);
  const end = Math.min(page * perPage, total);

  const isWebsite = theme === 'website';

  const btnStyle = (disabled) => ({
    padding: '6px 14px',
    borderRadius: 6,
    border: `1px solid ${isWebsite ? '#E5E5E3' : 'var(--border)'}`,
    background: isWebsite ? '#fff' : 'var(--bg2)',
    color: disabled ? (isWebsite ? '#B0B2B8' : 'var(--text3)') : (isWebsite ? '#14161C' : 'var(--text1)'),
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 12,
    fontWeight: 500,
    opacity: disabled ? 0.5 : 1,
    fontFamily: 'inherit',
  });

  const perPageBtn = (n) => ({
    padding: '4px 10px',
    borderRadius: 6,
    border: perPage === n
      ? `1px solid ${isWebsite ? '#2563EB' : 'var(--blue)'}`
      : `1px solid ${isWebsite ? '#E5E5E3' : 'var(--border)'}`,
    background: perPage === n
      ? (isWebsite ? '#2563EB' : 'var(--blue)')
      : (isWebsite ? '#fff' : 'var(--bg2)'),
    color: perPage === n ? '#fff' : (isWebsite ? '#52545C' : 'var(--text2)'),
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: perPage === n ? 600 : 400,
    fontFamily: 'inherit',
  });

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 0',
      fontSize: 13,
      color: isWebsite ? '#8B8D94' : 'var(--text2)',
      flexWrap: 'wrap',
      gap: 8,
    }}>
      {/* Left — showing count */}
      <span style={{ fontSize: 12, minWidth: 120 }}>
        Showing {start}–{end} of {total}
      </span>

      {/* Center — per page selector */}
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: isWebsite ? '#B0B2B8' : 'var(--text3)', marginRight: 6 }}>Per page:</span>
        {[10, 25, 50, 100].map(n => (
          <button key={n} onClick={() => onPerPageChange(n)} style={perPageBtn(n)}>
            {n}
          </button>
        ))}
      </div>

      {/* Right — prev/next */}
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1} style={btnStyle(page <= 1)}>
          ← Prev
        </button>
        <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages} style={btnStyle(page >= totalPages)}>
          Next →
        </button>
      </div>
    </div>
  );
}

export default Pagination;
